import type { Tier, BillingPeriod } from "@mw/shared/pricing";
import {
  TIERS,
  getMusicApiUsdPerMinute,
  getPricingTiers,
  getSubscriptionPrice,
  type PricingDataOptions,
} from "@mw/shared/pricing";
import type { StaticImageData } from "next/image";

import { getBusinessMaxMinutes } from "./PricingCalculator/calculatorUtils";
import {
  FlashCover,
  MultilingualCover,
  ScribeCover,
  ScribeRealtimeCover,
  MusicCover,
  VoiceChangerCover,
  VoiceIsolatorCover,
  SFXCover,
  DubbingCover,
} from "./covers";

const paygTier = TIERS.payg;

function getPaygNativeRates(options: PricingDataOptions = {}) {
  const api = paygTier.platformConfig.api;
  return {
    ttsMultilingualPer1KChars: api.textToSpeech.multilingualUsdPer1000Chars,
    ttsFlashPer1KChars: api.textToSpeech.turboUsdPer1000Chars,
    sttScribePerHour: api.speechToText.scribeUsdPerHour,
    sttScribeRealtimePerHour: api.speechToText.scribeRealtimeUsdPerHour,
    musicPerMinute: getMusicApiUsdPerMinute(options),
    voiceIsolatorPerMinute: api.voiceIsolator.usdPerMinute,
    voiceChangerPerMinute: api.voiceChanger.usdPerMinute,
    soundEffectsPerMinute: api.soundEffects.usdPerMinute,
    dubbingPerMinute: api.dubbing.autoUsdPerMinute,
  };
}

export type PaygProductId =
  | "tts_flash"
  | "tts_multilingual"
  | "stt_scribe"
  | "stt_realtime"
  | "music"
  | "voice_isolator"
  | "voice_changer"
  | "sound_effects"
  | "dubbing";

import type { PaygUnit } from "./PricingCalculator/calculatorUtils";

export interface PaygProductOption {
  id: PaygProductId;
  unit: PaygUnit;
  /** USD per native unit. For "characters" this is per 1,000 characters. */
  pricePerUnit: number;
  /** Slider ceiling, in `unit`. */
  maxValue: number;
  /** Sensible default when first selecting this product. */
  defaultValue: number;
  /** Cover image rendered on the calculator's PAYG result card. */
  coverImage: StaticImageData;
}

// Aligned with the subscription calculator's Business-tier ceiling (~6K min = 6M credits / 1000 cpm).
const MAX_MINUTES = getBusinessMaxMinutes(TIERS);
const DEFAULT_MINUTES = 50;
// Mirror the minutes ceiling at the industry-standard 1K chars / minute of generated speech.
const MAX_CHARACTERS = MAX_MINUTES * 1000;
const DEFAULT_CHARACTERS = 50_000;
const MAX_HOURS = 10_000;
const DEFAULT_HOURS = 50;

// Round Multilingual default usage that anchors the calculator on Creator-tier
// territory (PAYG cost sits comfortably under the Creator monthly price).
const MULTILINGUAL_DEFAULT_CHARACTERS = 100_000;

const COVER_IMAGES: Record<PaygProductId, StaticImageData> = {
  tts_flash: FlashCover,
  tts_multilingual: MultilingualCover,
  stt_scribe: ScribeCover,
  stt_realtime: ScribeRealtimeCover,
  music: MusicCover,
  voice_isolator: VoiceIsolatorCover,
  voice_changer: VoiceChangerCover,
  sound_effects: SFXCover,
  dubbing: DubbingCover,
};

const DEFAULT_PAYG_PRODUCT_ID: PaygProductId = "tts_flash";

export function getPaygProductOptions(options: PricingDataOptions = {}): PaygProductOption[] {
  const rates = getPaygNativeRates(options);
  const minutesProduct = (id: PaygProductId, pricePerUnit: number): PaygProductOption => ({
    id,
    unit: "minutes",
    pricePerUnit,
    maxValue: MAX_MINUTES,
    defaultValue: DEFAULT_MINUTES,
    coverImage: COVER_IMAGES[id],
  });
  const charactersProduct = (
    id: PaygProductId,
    pricePerUnit: number,
    defaultValue: number = DEFAULT_CHARACTERS,
  ): PaygProductOption => ({
    id,
    unit: "characters",
    pricePerUnit,
    maxValue: MAX_CHARACTERS,
    defaultValue,
    coverImage: COVER_IMAGES[id],
  });
  const hoursProduct = (id: PaygProductId, pricePerUnit: number): PaygProductOption => ({
    id,
    unit: "hours",
    pricePerUnit,
    maxValue: MAX_HOURS,
    defaultValue: DEFAULT_HOURS,
    coverImage: COVER_IMAGES[id],
  });

  return [
    charactersProduct("tts_flash", rates.ttsFlashPer1KChars),
    charactersProduct("tts_multilingual", rates.ttsMultilingualPer1KChars, MULTILINGUAL_DEFAULT_CHARACTERS),
    hoursProduct("stt_scribe", rates.sttScribePerHour),
    hoursProduct("stt_realtime", rates.sttScribeRealtimePerHour),
    minutesProduct("music", rates.musicPerMinute),
    minutesProduct("voice_isolator", rates.voiceIsolatorPerMinute),
    minutesProduct("voice_changer", rates.voiceChangerPerMinute),
    minutesProduct("sound_effects", rates.soundEffectsPerMinute),
    minutesProduct("dubbing", rates.dubbingPerMinute),
  ];
}

/** O(1) lookup for a PAYG product by id. Falls back to the default product if `id` is unknown. */
export function getPaygProduct(id: PaygProductId, options: PricingDataOptions = {}) {
  const products = getPaygProductOptions(options);
  const byId = Object.fromEntries(products.map(p => [p.id, p])) as Record<PaygProductId, PaygProductOption>;
  return byId[id] ?? byId[DEFAULT_PAYG_PRODUCT_ID];
}

export function computePaygCostUsd(product: PaygProductOption, usage: number): number {
  if (product.unit === "characters") return (usage * product.pricePerUnit) / 1000;
  return usage * product.pricePerUnit;
}

/**
 * Value-equivalent units of `product` for `tier`'s subscription dollars at PAYG rates.
 *
 * This is a "would you save vs PAYG?" heuristic — NOT the literal included credit
 * allowance. It answers: "if you spent the subscription's monthly price at PAYG
 * rates, how many units of `product` would you get?" The recommended-tier picker
 * uses this to find the cheapest plan whose dollar value beats the user's PAYG
 * cost. Real per-product included amounts vary across tiers (see tier limits in
 * shared/pricing.ts) and don't map cleanly to PAYG units for every product
 * (e.g. SFX is per-generation in tiers, per-minute in PAYG).
 */
export function tierPaygValueUnits(tier: Tier, product: PaygProductOption): number | undefined {
  // Always base the per-product value on the monthly list price, not the yearly
  // discounted equivalent. The plan's included allowance doesn't shrink when a
  // user picks yearly billing — they get the same units for a lower price — so
  // the "X characters included" bullet must stay stable across billing periods.
  const subscriptionPrice = getSubscriptionPrice(tier, "monthly");
  if (!subscriptionPrice || product.pricePerUnit <= 0) return undefined;
  const raw = subscriptionPrice / product.pricePerUnit;
  return Math.round(product.unit === "characters" ? raw * 1000 : raw);
}

export interface PaygUsageInput {
  product: PaygProductOption;
  usage: number;
}

interface MultiSubscriptionRecommendation {
  tier: Tier;
  monthlyPrice: number | undefined;
  isEnterprise: boolean;
  /** Aggregate PAYG cost used to drive the recommendation. */
  totalPaygCostUsd: number;
}

// Pure-dollar aggregation works because API tiers have one global `includedCredits`
// pool (no per-product credit rates), so the meaningful question is "which tier's
// monthly price first beats the summed PAYG cost?".
export function recommendSubscriptionTierMulti(
  usages: PaygUsageInput[],
  period: BillingPeriod,
): MultiSubscriptionRecommendation {
  const totalPaygCostUsd = usages.reduce((sum, { product, usage }) => sum + computePaygCostUsd(product, usage), 0);
  const tiers = getPricingTiers();
  // Tier *selection* uses the monthly list price (the value the plan delivers),
  // since the yearly discount doesn't change the included allowance. The card
  // still *displays* the period-specific price.
  const candidates = tiers
    .filter(t => t.planId !== "enterprise" && t.planId !== "payg" && t.planId !== "free")
    .map(tier => ({
      tier,
      thresholdPrice: getSubscriptionPrice(tier, "monthly"),
      displayPrice: getSubscriptionPrice(tier, period),
    }))
    .filter(
      (c): c is typeof c & { thresholdPrice: number; displayPrice: number } =>
        c.thresholdPrice !== undefined && c.displayPrice !== undefined,
    )
    .sort((a, b) => a.thresholdPrice - b.thresholdPrice);

  const fit = candidates.find(c => c.thresholdPrice >= totalPaygCostUsd);
  if (fit) {
    return {
      tier: fit.tier,
      monthlyPrice: fit.displayPrice,
      isEnterprise: false,
      totalPaygCostUsd,
    };
  }

  const enterprise = tiers.find(t => t.planId === "enterprise");
  return {
    tier: enterprise ?? candidates[candidates.length - 1].tier,
    monthlyPrice: undefined,
    isEnterprise: enterprise !== undefined,
    totalPaygCostUsd,
  };
}
